import { helpInfo, CloseWhite, close, search } from "helper/icons";
import { useRef, useEffect, useState } from "react";
import debounce from "lodash/debounce";
import { IconButton } from "shared/Components/iconButton";

export const SearchDropdown = (props) => {
  const {
    label,
    placeholder,
    errorMsg,
    showCancel = true,
    width = "w-[358px] lg:w-[384px] max-[400px]:w-[100%]",
    dropdownOptions = [],
    onSelectOption,
    onChangeSearchText,
    isShowClearButton = false,
    onClickClearButton,
    defaultValue = "",
    isFaded = false,
    height,
    disabled = false,
    decoratedValue = (value) => {
      return value;
    },
    isShadow = false,
  } = props || {};
  const [searchTerm, setSearchTerm] = useState("");
  const [isShowMenu, setIsShowMenu] = useState(false);
  const [isOptionSelected, setIsOptionSelected] = useState(false);
  const [filterDropdownOptions, setFilterDropdownOptions] = useState([]);
  const dropdownRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    setSearchTerm(defaultValue === null ? "" : defaultValue);
  }, [defaultValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsShowMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const filteredItem = dropdownOptions.filter((option) => {
      return option?.toLowerCase().includes(searchTerm?.toLowerCase());
    });
    setFilterDropdownOptions(filteredItem);
  }, [dropdownOptions, searchTerm]);

  const selectOption = (option, index) => {
    setIsShowMenu(false);
    setSearchTerm(decoratedValue(option));
    setIsOptionSelected(true);
    onSelectOption?.(option, index);
  };

  function handleClearRequest() {
    setIsShowMenu(false);
    setIsOptionSelected(false);
    setSearchTerm("");
    onSelectOption?.();
  }

  const debouncedSearch = useRef(
    debounce((value) => {
      setFilterDropdownOptions(
        dropdownOptions.filter((option) => {
          return option?.toLowerCase().includes(value?.toLowerCase());
        })
      );
      onChangeSearchText(value);
    }, 1000)
  ); // 300 milliseconds debounce delay, adjust as needed

  const handleInputChange = (event) => {
    if (!isOptionSelected) {
      const value = event.target.value;
      setSearchTerm(value);
      debouncedSearch.current(value); // trigger the debounce function
    }
  };

  return (
    <div className={`relative flex flex-col ${width}`} ref={dropdownRef}>
      {!!label && (
        <div className="mb-0.5">
          <span className="text-gray-700 font-medium text-sm">{label}</span>
        </div>
      )}
      <div
        className={`flex flex-row gap-2 ${
          label ? `mt-1` : ""
        } flex-1 items-stretch relative`}
      >
        <div className={`flex row flex-1 flex-grow relative w-full`}>
          <input
            autoComplete="off"
            placeholder={placeholder}
            value={searchTerm}
            className={`${height} w-full rounded-md ${!!isShadow && "shadow-100"} border ${
              !isOptionSelected && errorMsg ? "border-red" : "border-gray-300"
            } py-2.5 pl-3.5 pr-10 ${
              disabled ? "text-gray-900/50" : "text-gray-900"
            } font-inter font-normal text-base inputFocus focus:outline-none ${
              isFaded ? "bg-white" : "bg-white"
            }`}
            onChange={handleInputChange}
            onFocus={() => setIsShowMenu(true)}
            disabled={disabled}
          />
          {searchTerm && showCancel && !disabled ? (
            <img
              onClick={() => {
                handleClearRequest();
              }}
              src={close}
              alt=""
              height={20}
              width={20}
              className="absolute top-3 right-4 cursor-pointer"
            />
          ) : (
            <img
              src={search}
              alt=""
              height={20}
              width={20}
              className="absolute top-3 right-4"
            />
          )}
          {placeholder == "Breed" && (
          <div
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <img src={helpInfo} alt="Help" className="cursor-pointer absolute top-[14px] right-10" />
            {isHovered && (
              <div className="absolute -bottom-24 right-0 w-64 p-3 bg-white shadow-100 border border-gray-300 rounded-lg z-[99999]">
                <p className="text-sm text-gray-900">
                  🐶 If your breed isn't listed, please choose "Other" and type the breed in the box below.
                </p>
              </div>
            )}
          </div>
          )}
        </div>
        {isShowClearButton ? (
          <IconButton
            icon={CloseWhite}
            width={"w-[40px]"}
            onClick={() => {
              onClickClearButton?.();
            }}
          />
        ) : null}
        {isShowMenu && filterDropdownOptions.length > 0 && (
          <div
            className={`origin-top-left absolute left-0 top-10  mt-2 rounded-md shadow-100 bg-white ${width} z-50 max-h-52 overflow-auto`}
          >
            <div
              className="py-1"
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="options-menu"
            >
              {filterDropdownOptions?.map((option, index) => (
                <button
                  key={option}
                  onClick={() => selectOption(option, index)}
                  className={`block w-full text-left px-4 py-4 text-sm text-gray-700 hover:bg-primary/50 hover:text-white ${
                    index !== filterDropdownOptions?.length - 1
                      ? "border-b border-purple-100"
                      : ""
                  } border-gray-200`}
                  role="menuitem"
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!!errorMsg && (
        <span className="text-red font-medium text-sm mt-1 pt-2">
          {errorMsg}
        </span>
      )}
    </div>
  );
};
